"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, light = false }: { text: string; light?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase mb-3.5 ${light ? "text-blue-300" : "text-blue-700"}`}>
      {text}
    </span>
  );
}
function Divider({ light = false }) {
  return <div className={`w-11 h-[3px] mb-6 ${light ? "bg-blue-400" : "bg-blue-700"}`} />;
}

/* ─── Data ────────────────────────────────────────────────── */
const AI_STATS = [
  { v: "3.7x",  l: "ROI from AI adoption (avg.)",          src: "McKinsey 2024" },
  { v: "40%",   l: "Productivity gain from AI automation",  src: "Accenture" },
  { v: "80%",   l: "Of AI value from 20% of use cases",     src: "Gartner" },
  { v: "2030",  l: "AI to add $15.7T to global economy",    src: "PwC" },
];

const SERVICES = [
  {
    n: "01", title: "AI Strategy & Consulting",
    body: "Define your AI roadmap. We assess your data maturity, identify high-impact use cases and build a practical AI implementation strategy aligned with your business goals and existing infrastructure.",
    img: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&q=80",
    tags: ["AI Roadmap", "Data Maturity Audit", "Use Case Discovery"],
  },
  {
    n: "02", title: "Custom ML Model Development",
    body: "End-to-end machine learning — data collection, preprocessing, feature engineering, model training, validation, deployment and monitoring. Models built for your specific data and objectives.",
    img: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&q=80",
    tags: ["Model Training", "Feature Engineering", "MLOps"],
  },
  {
    n: "03", title: "Natural Language Processing (NLP)",
    body: "Text classification, sentiment analysis, named entity recognition, document summarization, intelligent search, language translation and AI-powered chatbots for customer service and internal knowledge management.",
    img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80",
    tags: ["Sentiment Analysis", "NER", "Document AI"],
  },
  {
    n: "04", title: "Computer Vision",
    body: "Image and video analysis — object detection, facial recognition, document OCR, quality inspection for manufacturing, medical image analysis and visual search for e-commerce.",
    img: "https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=600&q=80",
    tags: ["Object Detection", "OCR", "Quality Inspection"],
  },
  {
    n: "05", title: "Predictive Analytics",
    body: "Demand forecasting, customer churn prediction, fraud detection, price optimization, credit risk scoring and inventory optimization — decisions powered by data, not intuition.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    tags: ["Churn Prediction", "Fraud Detection", "Forecasting"],
  },
  {
    n: "06", title: "Process Automation (RPA + AI)",
    body: "Intelligent automation combining Robotic Process Automation with AI — automate complex rule-based and decision-making tasks, document processing, data entry, report generation and workflow routing.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["RPA", "Intelligent Automation", "Workflow AI"],
  },
  {
    n: "07", title: "AI-Powered Chatbots",
    body: "Customer service chatbots, internal HR bots, sales assistant bots — built on GPT-4, Claude, or open-source LLMs, integrated with your CRM, WhatsApp and website.",
    img: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&q=80",
    tags: ["GPT-4 / Claude", "WhatsApp Bot", "CRM Integration"],
  },
  {
    n: "08", title: "Business Intelligence Dashboards",
    body: "AI-augmented BI dashboards with natural language querying, automated insight generation, anomaly detection and trend forecasting — making your data accessible to every decision-maker.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["NL Querying", "Anomaly Detection", "Auto Insights"],
  },
];

const TECH_STACK = [
  { tech: "Python (TensorFlow, PyTorch, Scikit-learn)", use: "Core ML model development and training" },
  { tech: "OpenAI GPT-4 / Claude API",                  use: "Conversational AI, content generation, reasoning" },
  { tech: "LangChain & LlamaIndex",                     use: "LLM application development, RAG pipelines" },
  { tech: "Hugging Face Transformers",                   use: "NLP models: BERT, RoBERTa, fine-tuning" },
  { tech: "Apache Spark / Databricks",                   use: "Large-scale data processing and ML pipelines" },
  { tech: "MLflow / Weights & Biases",                   use: "Model tracking, versioning and monitoring" },
  { tech: "FastAPI + Docker",                            use: "ML model serving and deployment as APIs" },
  { tech: "AWS SageMaker / Azure ML",                    use: "Managed ML platform for training and deployment" },
];

const INDUSTRIES = [
  { industry: "Healthcare",          cases: "Diagnostic image analysis, patient outcome prediction, drug interaction checking" },
  { industry: "Retail & E-commerce", cases: "Demand forecasting, personalized recommendations, dynamic pricing, visual search" },
  { industry: "Finance & Banking",   cases: "Fraud detection, credit scoring, algorithmic trading, regulatory compliance" },
  { industry: "Manufacturing",       cases: "Quality control (computer vision), predictive maintenance, yield optimization" },
  { industry: "HR & Recruitment",    cases: "CV screening, candidate matching, attrition prediction, employee sentiment" },
  { industry: "Agriculture",         cases: "Crop yield prediction, disease detection (image analysis), weather impact modeling" },
];

const FAQS = [
  { q: "Does our business have enough data to benefit from AI?",                  a: "Most businesses have more usable data than they realise — in their CRM, ERP, transactions and documents. Our AI Strategy engagement begins with a data maturity assessment to identify what you have, what gaps exist and which use cases are viable right now versus in 6–12 months. You don't need perfect data to start — you need a realistic roadmap." },
  { q: "What is the difference between AI, ML and data analytics?",              a: "Data analytics describes and summarises what has happened. Machine learning identifies patterns in historical data to make predictions about what will happen. AI is the broader field encompassing ML plus reasoning, language understanding and decision-making. In practice, most business solutions combine all three — analytics for insight, ML for prediction and AI for action." },
  { q: "How long does it take to build and deploy a custom ML model?",            a: "A focused ML model — for example, a churn prediction or demand forecasting model — typically takes 8–16 weeks from data assessment to production deployment. More complex systems involving NLP pipelines, computer vision, or LLM integration take 3–6 months. We use a phased approach: a working prototype first, then iterative refinement." },
  { q: "What is a RAG pipeline and when do we need one?",                         a: "RAG (Retrieval-Augmented Generation) combines a large language model with your proprietary knowledge base — documents, manuals, policies, product catalogues. Instead of a generic chatbot, you get an AI assistant that answers accurately from your own data. It's ideal for internal knowledge management, customer support bots and document Q&A systems." },
  { q: "How do you ensure our AI models remain accurate over time?",              a: "Models degrade as real-world data distributions shift — this is called model drift. We implement monitoring pipelines using MLflow or Weights & Biases that track prediction accuracy, data drift and concept drift in production. Retraining schedules and alerting thresholds are defined at deployment so performance stays within agreed bounds." },
  { q: "Is our data safe when used to train AI models?",                          a: "All model development is conducted within your approved infrastructure or our secure, isolated cloud environments. We never use your data to train shared models or expose it to third parties. For regulated industries, we support on-premise deployment and full data residency requirements, including compliance with India's DPDP Act." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard({ s, i }: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 4) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[160px] overflow-hidden">
        <img
          src={s.img} alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[13.5px] font-bold text-slate-900 mb-2 leading-snug">{s.title}</p>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-3.5">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Table Row ───────────────────────────────────────────── */
function TableRow(props: { col1: string; col2: string; i: number; isFirst: boolean }) {
  const { col1, col2, i, isFirst } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`grid border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${isFirst ? "border-t" : ""}`}
      style={{ transitionDelay: `${i * 0.04}s`, gridTemplateColumns: "260px 1fr" }}
    >
      <div className="py-4 px-5 bg-slate-50 border-r border-slate-200 flex items-center">
        <span className="text-[12px] font-bold text-slate-700 tracking-[0.05em] uppercase leading-snug">{col1}</span>
      </div>
      <div className="py-4 px-5 flex items-center">
        <span className="text-[13.5px] text-slate-500 leading-relaxed">{col2}</span>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function AIMLPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>AI & ML</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["Custom ML Models", "NLP", "Computer Vision", "Predictive Analytics", "LLM / RAG", "RPA"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Artificial Intelligence & Machine Learning" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Unlock Business Intelligence — AI & Machine Learning That Delivers Real Results
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Transform your raw data into strategic advantage. We build custom AI models, intelligent automation systems and predictive analytics solutions that help Indian businesses compete in the intelligence economy.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              BOOK AI CONSULTATION
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              FREE DATA ASSESSMENT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/AI.png"
            alt="Artificial intelligence and machine learning"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "3.7x",  l: "Average AI ROI" },
          { v: "8",     l: "AI & ML Services" },
          { v: "40%",   l: "Productivity Gain" },
          { v: "Free",  l: "Data Assessment" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ WHY AI NOW ═════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-900">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="The AI Opportunity" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-white leading-tight mb-5">
              Your Competitors Are Already Using AI
            </h2>
            <p className="text-[15px] font-light text-slate-300 leading-relaxed mb-8">
              AI adoption is no longer a future consideration — it is the present competitive landscape. Businesses using AI for forecasting, automation and customer intelligence are pulling ahead. Those that wait are not standing still; they are falling behind.
            </p>
            <div className="flex flex-col gap-4">
              {[
                "AI delivers an average 3.7x ROI — with top performers seeing 10x+ returns (McKinsey 2024)",
                "40% productivity gain achievable through intelligent automation of knowledge work (Accenture)",
                "80% of AI business value typically comes from just 20% of the right use cases (Gartner)",
                "PwC projects AI will add $15.7 trillion to the global economy by 2030",
                "India's AI market is projected to reach $17 billion by 2027 — early movers capture the advantage",
              ].map((stat, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[14px] text-slate-300 leading-relaxed">{stat}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="grid grid-cols-2 gap-0.5">
              {AI_STATS.map((s, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6">
                  <p className="text-[32px] font-black text-blue-400 leading-none mb-2">{s.v}</p>
                  <p className="text-[13px] text-slate-300 leading-snug mb-2">{s.l}</p>
                  <p className="text-[11px] font-semibold text-slate-500 tracking-wide uppercase">{s.src}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ SERVICES — 4-col grid ══════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="AI & ML Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Eight AI Capabilities.<br />One Intelligent Business.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From strategy through to custom model deployment, automation and BI — full-spectrum AI & ML coverage.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ TECH STACK TABLE ═══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Technology Stack" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              The Tools Behind Our AI Solutions
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              We work with the leading open-source and enterprise AI/ML frameworks — selecting the right stack for your data volume, latency requirements and cloud environment. No proprietary lock-in.
            </p>
            <img
              src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=700&q=80"
              alt="AI technology infrastructure"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="mt-1">
              {TECH_STACK.map((row, i) => (
                <TableRow key={i} col1={row.tech} col2={row.use} i={i} isFirst={i === 0} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ INDUSTRY APPLICATIONS TABLE ════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="right" delay={0.1}>
            <div className="mt-1">
              {INDUSTRIES.map((row, i) => (
                <TableRow key={i} col1={row.industry} col2={row.cases} i={i} isFirst={i === 0} />
              ))}
            </div>
          </Reveal>

          <Reveal from="left">
            <Eyebrow text="Industry Applications" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              AI Built for Your Industry
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              Every industry has distinct data patterns, regulatory constraints and decision-making workflows. We build AI solutions grounded in domain knowledge — not generic models retrofitted to your context.
            </p>
            <img
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=700&q=80"
              alt="AI industry applications"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About AI & ML
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear answers to the questions we hear most from businesses starting their AI journey or evaluating a specific AI use case.
            </p>
            <img
              src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=700&q=80"
              alt="AI consultation"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Build with AI?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Book a Free AI Consultation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              We assess your data maturity, map your highest-impact AI use cases and deliver a practical implementation roadmap — at no cost, no commitment required.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most businesses discover 2–3 AI opportunities with measurable ROI in their first assessment. The question is whether you act on them before your competitors do.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                BOOK AI CONSULTATION
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                FREE DATA ASSESSMENT
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}